import { useAppStore } from '@/store/useAppStore';
import { CheckIcon as Check } from './ui/Icons';
import { ValidationSeverity } from '@gpa-scales/shared-types';
import type { ValidationIssue } from '@gpa-scales/shared-types';

const EMPTY_ISSUES: readonly ValidationIssue[] = [];

export function ValidationPanel() {
  const issues = useAppStore((s) => s.result?.validationIssues ?? EMPTY_ISSUES);

  if (issues.length === 0) {
    return (
      <div className="rounded-lg border border-success/30 bg-success-light p-4 text-center">
        <p className="flex items-center justify-center gap-1 text-sm font-medium text-success">
          <Check className="h-4 w-4" aria-hidden="true" /> All clear — your data looks good
        </p>
      </div>
    );
  }

  const grouped = {
    [ValidationSeverity.Error]: issues.filter((i) => i.severity === ValidationSeverity.Error),
    [ValidationSeverity.Warning]: issues.filter((i) => i.severity === ValidationSeverity.Warning),
    [ValidationSeverity.Info]: issues.filter((i) => i.severity === ValidationSeverity.Info),
  };

  const severityConfig = {
    [ValidationSeverity.Error]: { icon: 'error', label: 'Errors', color: 'text-error', bg: 'bg-error-light' },
    [ValidationSeverity.Warning]: { icon: 'warning', label: 'Warnings', color: 'text-warning', bg: 'bg-warning-light' },
    [ValidationSeverity.Info]: { icon: 'info', label: 'Info', color: 'text-info', bg: 'bg-info-light' },
  };

  const SeverityIcon = ({ type }: { type: string }) => {
    if (type === 'error') return (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#dc2626"/><path d="M13 7L7 13M7 7l6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
    );
    if (type === 'warning') return (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2L1 18h18L10 2z" fill="#d97706"/><rect x="9" y="8" width="2" height="4" rx="1" fill="#fff"/><circle cx="10" cy="14" r="1" fill="#fff"/></svg>
    );
    return (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#0891b2"/><rect x="9" y="5" width="2" height="7" rx="1" fill="#fff"/><circle cx="10" cy="14" r="1.2" fill="#fff"/></svg>
    );
  };

  return (
    <div className="space-y-3">
      {([ValidationSeverity.Error, ValidationSeverity.Warning, ValidationSeverity.Info] as const).map(
        (severity) => {
          const items = grouped[severity];
          if (items.length === 0) return null;
          const config = severityConfig[severity];
          return (
            <div key={severity} className="rounded-lg border border-border bg-surface">
              <div className={`flex items-center gap-2 rounded-t-lg px-4 py-2 ${config.bg}`}>
                <SeverityIcon type={config.icon} />
                <span className={`text-sm font-medium ${config.color}`}>
                  {config.label} ({items.length})
                </span>
              </div>
              <ul className="divide-y divide-border">
                {items.map((issue) => (
                  <li key={issue.id} className="px-4 py-2">
                    <p className="text-sm text-[var(--color-text)]">{issue.message}</p>
                    {issue.suggestion && (
                      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                        <svg className="mr-1 inline h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 1a5 5 0 00-3 9v2a1 1 0 001 1h4a1 1 0 001-1v-2a5 5 0 00-3-9zM7 15v1a1 1 0 001 1h4a1 1 0 001-1v-1H7z" fill="#d97706"/></svg>
                        {issue.suggestion}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
      )}
    </div>
  );
}
